/* What the control plane says about itself.
 *
 * The first screen this product ever had, chosen deliberately over a mock catalog: it was the only
 * view backed by data that genuinely existed, and it renders the honest thing — capabilities with no
 * adapter, visible rather than inferred from a deployment doc.
 *
 * **Brought onto the design system 2026-08-25, and it was the last screen not on it.** It had its own
 * `<h2>` where every other screen has a `PageHeader`, and a hand-rolled `<table>` with inline padding
 * on every cell — so it was the one page whose rows were a different height from the rest of the
 * product, whose header row had no sticky behaviour, and which sat on the page background rather
 * than in a card. Being first is how it got missed: it predates the kit it should have been built
 * with.
 *
 * The fidelity is a `StatusBadge`, as the design draws it and as the scheduling table beneath it
 * already did: two treatments of one three-step scale on one page read as two different scales.
 * The words stay the scale's own, and the tone is a total map so a fourth fidelity is a compile
 * error rather than a pill in the healthy colour. */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Sections } from '@/shared/ui/Layout';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import type { BadgeTone } from '@/shared/ui/StatusBadge';
import { UnavailableState } from '@/shared/ui/States';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useReadiness, READINESS_URL } from '../api/useReadiness';
import { SchedulingStatus } from '@/features/scheduling';
import { ChainVerification } from './ChainVerification';
import type { Fidelity } from '../model/types';

/* Green only for production: a stub satisfies the type while doing nothing, which is the failure the
 * startup guard refuses in production, so the UI must not make it look healthy either. */
const FIDELITY_TONE = {
  production: 'ok',
  stub: 'warn',
  missing: 'danger',
} as const satisfies Record<Fidelity, BadgeTone>;

export function PlatformStatus(): ReactElement {
  const { t } = useTranslation();
  const { data, error, isPending } = useReadiness();

  if (isPending) return <SkeletonRows rows={4} />;

  if (error !== null) {
    return (
      <UnavailableState
        title={t('status.unreachableTitle')}
        guidance={t('status.unreachable', { url: READINESS_URL })}
      />
    );
  }

  const isReady = data.status === 'ready';

  return (
    <Sections>
      <PageHeader
        title={t('status.title')}
        lede={t('status.degradedCount', { count: data.degraded.length })}
        /* The verdict and the profile sit in the header's action slot rather than beside the title
           in a hand-built flex row: a badge and a profile name are the page's *status*, which is
           what that slot is for, and it keeps the title on the same baseline as every other page. */
        actions={
          <>
            <StatusBadge
              tone={isReady ? 'ok' : 'warn'}
              label={isReady ? t('status.ready') : t('status.degraded')}
            />
            <span className="lq-muted lumiq-numeric">
              {t('status.profile')}: {data.profile}
            </span>
          </>
        }
      />

      {data.degraded.length > 0 ? (
        <Card title={t('status.fidelityTitle')}>
          <DataTable
            caption={t('status.fidelityCaption')}
            columns={[
              { key: 'capability', label: t('status.capability') },
              { key: 'fidelity', label: t('status.fidelity') },
              { key: 'detail', label: t('status.detail') },
            ]}
          >
            {data.degraded.map((entry) => (
              <tr key={entry.capability}>
                <td className="lq-mono">{entry.capability}</td>
                <td>
                  <StatusBadge
                    tone={FIDELITY_TONE[entry.fidelity]}
                    label={t(`status.fidelityLabel.${entry.fidelity}`)}
                  />
                </td>
                <td className="lq-muted">{entry.detail}</td>
              </tr>
            ))}
          </DataTable>
        </Card>
      ) : null}

      {/* Below the capability table, on the same screen, because it answers the adjacent question.
       * The table says whether chain verification *can* run; this says what it *found*. An operator
       * reading "chain_verification: stub" above and a 503 below has the whole story in one place. */}
      {/* **The two schedulers, and four states rather than two.** Whether scheduled and
          after-harvest work survives a restart is the question an operator comes to this screen
          with, and `production` / `stub` / `missing` / `unknown` each send them somewhere
          different: a stub still runs the work, so reporting it as "scheduling is off" would be
          the wrong place entirely. Below the fidelity table because it reads the same response
          and answers the adjacent, sharper question. */}
      <SchedulingStatus degraded={data.degraded} title={t('scheduling.platform.title')} />

      <ChainVerification />
    </Sections>
  );
}
